import {
  SETUP_GOALS,
  RULES_CONSTRAINTS,
  type SetupGoal,
  CONCENTRATION_TOOLTIP,
  NET_TOOLTIP,
  MAX_TURNOVER_TOOLTIP,
  MAX_TRADE_TOOLTIP,
  EQUITY_CHANGE_TOOLTIP,
  ALLOW_DIRECTION_CHANGE_TOOLTIP,
  TRADE_BASKETS_TOOLTIP,
} from "connected-components/optimize-experiments/constants"
import { type ConcentrationConstraint, type MaxTradeConstraint } from "connected-components/optimize-experiments/hooks/types"
import type { FactorRuleDisplay } from "connected-components/optimize-experiments/components/factor-rules/types"
import type { SecuritySearchFilter } from "resources/filters"

type StringMaxTradeConstraint<T> = {
  [K in keyof T]: T[K] extends number ? string : T[K]
}

export type MaxTradeConstraintInput = StringMaxTradeConstraint<MaxTradeConstraint>
export type ConcentrationConstraintInput = StringMaxTradeConstraint<ConcentrationConstraint>

export interface SetupSection {
  goal: SetupGoal
  targetTotalRiskValue?: string
  forecast?: string
}

export interface RulesSection {
  tradeSwaps: boolean | null
  maxTurnover: string | null
  concentration: ConcentrationConstraintInput | null
  maxTrade: MaxTradeConstraintInput | null
  equityChange: string | null
  netLong: { longMarketValue: string, shortMarketValue: number } | null
  allowDirectionChange: boolean | null
  exposure: FactorRuleDisplay[] | null
  composition: FactorRuleDisplay[] | null
}

interface UniverseOption {
  filter?: SecuritySearchFilter
  value: string
  count?: number
}

export interface UniverseSection {
  long: UniverseOption | null
  short: UniverseOption | null
}

export interface FormSections {
  setup: SetupSection
  rules: RulesSection
  universe: UniverseSection
}

export interface ConstraintInput {
  fieldKey: string
  unit: string
  label?: string
  disabled?: boolean
}

export interface ConstraintConfig<TValue> {
  label: string
  tooltip?: string
  defaultValue: TValue | null
  inputs?: ConstraintInput[]
}

export const FORM_CONSTRAINTS = {
  [RULES_CONSTRAINTS.MAX_TURNOVER]: {
    label: "Max turnover",
    tooltip: MAX_TURNOVER_TOOLTIP,
    defaultValue: "20",
    inputs: [
      {
        fieldKey: "maxTurnover",
        unit: "%",
      },
    ],
  } satisfies ConstraintConfig<string>,
  [RULES_CONSTRAINTS.CONCENTRATION]: {
    label: "Concentration",
    tooltip: CONCENTRATION_TOOLTIP,
    defaultValue: {
      minConcentration: "0",
      maxConcentration: "100",
    },
    inputs: [
      {
        fieldKey: "minConcentration",
        unit: "%",
        label: "Min",
      },
      {
        fieldKey: "maxConcentration",
        unit: "%",
        label: "Max",
      },
    ],
  } satisfies ConstraintConfig<ConcentrationConstraintInput>,
  [RULES_CONSTRAINTS.MAX_TRADE]: {
    label: "Max trade",
    tooltip: MAX_TRADE_TOOLTIP,
    defaultValue: {
      percentADV: "25",
      percentEquity: "25",
      percentOriginalEconomicExposure: "25",
    },
    inputs: [
      {
        fieldKey: "percentADV",
        unit: "% ADV",
      },
      {
        fieldKey: "percentEquity",
        unit: "% Eq",
      },
      {
        fieldKey: "percentOriginalEconomicExposure",
        unit: "% Rel",
      },
    ],
  } satisfies ConstraintConfig<MaxTradeConstraintInput>,
  [RULES_CONSTRAINTS.EQUITY_CHANGE]: {
    label: "Equity change",
    defaultValue: "3",
    tooltip: EQUITY_CHANGE_TOOLTIP,
    inputs: [
      {
        fieldKey: "equityChange",
        unit: "% Equity",
      },
    ],
  } satisfies ConstraintConfig<string>,
  [RULES_CONSTRAINTS.NET_LONG]: {
    label: "Net",
    tooltip: NET_TOOLTIP,
    defaultValue: { longMarketValue: "0", shortMarketValue: 0 },
    inputs: [
      {
        fieldKey: "longMarketValue",
        unit: "% Equity",
        label: "Long",
      },
      {
        fieldKey: "shortMarketValue",
        unit: "% Equity",
        label: "Short",
        disabled: true,
      },
    ],
  } satisfies ConstraintConfig<{ longMarketValue: string, shortMarketValue: number }>,
  [RULES_CONSTRAINTS.TRADE_SWAPS]: {
    label: "Trade baskets",
    tooltip: TRADE_BASKETS_TOOLTIP,
    defaultValue: true,
  } satisfies ConstraintConfig<boolean>,
  [RULES_CONSTRAINTS.ALLOW_DIRECTION_CHANGE]: {
    label: "Allow direction change",
    tooltip: ALLOW_DIRECTION_CHANGE_TOOLTIP,
    defaultValue: false,
  } satisfies ConstraintConfig<boolean>,
}

const DEFAULT_SETUP: SetupSection = {
  goal: SETUP_GOALS.MAXIMIZE_SPECIFIC_RISK,
  forecast: "none",
}

export const DEFAULT_UNIVERSE: UniverseSection = {
  long: null,
  short: null,
}

const DEFAULT_RULES: RulesSection = {
  tradeSwaps: null,
  maxTurnover: null,
  concentration: null,
  maxTrade: null,
  equityChange: null,
  netLong: null,
  allowDirectionChange: null,
  exposure: null,
  composition: null,
}

export const getDefaultConfiguration = (): FormSections => {
  return {
    setup: DEFAULT_SETUP,
    rules: DEFAULT_RULES,
    universe: DEFAULT_UNIVERSE,
  }
}
